import {useState, useMemo} from 'react'
import type {Turn, TurnItem, SubagentGroupItem} from '../../../types/events'
import type {EventToolCallEntry, ToolGroup} from '../tool-grouping/types'
import {AssistantText} from './AssistantText'
import {ToolCall} from './ToolCall'
import {AskUser} from './AskUser'
import {Permission} from './Permission'
import {Elicitation} from './Elicitation'
import {PlanApproval} from './PlanApproval'
import {RemoteSteerable} from './RemoteSteerable'
import {UserMessage} from './UserMessage'
import {groupTurnItems, pairedToolCallToToolMetadata} from '../../../utils/events/tool-helpers'
import {isTurnItemVisible, isUserMessageVisible} from '../../../utils/events/visibility'
import {EDIT_TOOLS} from '../../../constants/tool-names'
import {ToolGroupRenderer} from '../tool-grouping/ToolGroupRenderer'
import {createGroupHeader} from '../tool-grouping/header-strategies'
import {ExpandableMarkdown} from '../../expandable-markdown/ExpandableMarkdown'
import {SubagentSessionGroup} from '../SubagentSessionGroup/SubagentSessionGroup'
import turnStyles from '../EventSessionLogs.module.css'

function TurnItemView({item, mcpToolNameMappings}: {item: TurnItem; mcpToolNameMappings?: Record<string, string>}) {
  switch (item.kind) {
    case 'assistant_text':
      return <AssistantText item={item} />
    case 'tool_call':
      return <ToolCall item={item} mcpToolNameMappings={mcpToolNameMappings} />
    case 'ask_user':
      return <AskUser item={item} />
    case 'permission':
      return <Permission item={item} />
    case 'elicitation':
      return <Elicitation item={item} />
    case 'plan_approval':
      return <PlanApproval item={item} />
    case 'subagent_group':
      return <SubagentGroup item={item} mcpToolNameMappings={mcpToolNameMappings} />
    case 'remote_steerable':
      return <RemoteSteerable item={item} />
    default:
      return null
  }
}

function getLastToolTitle(group: SubagentGroupItem['group']): string | null {
  for (let i = group.turns.length - 1; i >= 0; i--) {
    const turn = group.turns[i]
    if (!turn) continue
    for (let j = turn.items.length - 1; j >= 0; j--) {
      const item = turn.items[j]
      if (item?.kind === 'tool_call') {
        return item.toolCall.toolName
      }
    }
  }
  return null
}

/**
 * Renders a collapsible subagent group with recursive turn rendering.
 */
function SubagentGroup({
  item,
  mcpToolNameMappings,
}: {
  item: SubagentGroupItem
  mcpToolNameMappings?: Record<string, string>
}) {
  const [isOpen, setIsOpen] = useState(false)
  const {group} = item

  const title = group.description
    ? `${group.agentDisplayName}: ${group.description}`
    : `Subagent: ${group.agentDisplayName}`

  const lastToolTitle = useMemo(() => getLastToolTitle(group), [group])
  const promptText = group.prompt || group.turns[0]?.userMessage?.content

  return (
    <SubagentSessionGroup
      title={title}
      open={isOpen}
      pending={group.isPending}
      statusText={group.isPending ? (lastToolTitle ?? undefined) : undefined}
      prompt={promptText ? <ExpandableMarkdown markdown={promptText} /> : undefined}
      onOpenChange={setIsOpen}
    >
      {group.turns.map((turn, idx) => (
        <TurnView
          key={turn.turnId ?? `subagent-turn-${idx}`}
          turn={turn}
          isSubagent
          mcpToolNameMappings={mcpToolNameMappings}
        />
      ))}
    </SubagentSessionGroup>
  )
}

/**
 * Renders a single turn: optional user message followed by all turn items.
 * Consecutive same-name tool calls are collapsed into groups.
 * Used both at the top level and recursively inside SubagentGroup.
 */
export function TurnView({
  turn,
  isSubagent = false,
  mcpToolNameMappings,
}: {
  turn: Turn
  isSubagent?: boolean
  mcpToolNameMappings?: Record<string, string>
}) {
  const groups = useMemo(() => groupTurnItems(turn.items), [turn.items])
  const groupHeaderFn = useMemo(() => createGroupHeader(mcpToolNameMappings), [mcpToolNameMappings])

  const showUserMessage = !isSubagent && turn.userMessage != null && isUserMessageVisible(turn.userMessage)

  if (!showUserMessage && !turn.items.some(isTurnItemVisible)) {
    return null
  }

  return (
    <div data-turn-id={turn.turnId} className={turnStyles.turn}>
      {turn.userMessage && !isSubagent && <UserMessage message={turn.userMessage} />}
      {groups.map(group => {
        if (group.type === 'single') {
          return <TurnItemView key={group.item.id} item={group.item} mcpToolNameMappings={mcpToolNameMappings} />
        }

        const isEditGroup = (EDIT_TOOLS as readonly string[]).includes(group.toolName)
        const entries: EventToolCallEntry[] = group.items.map(item => {
          const metadata = pairedToolCallToToolMetadata(item.toolCall)
          return {
            type: 'event-tool-call',
            toolCallMetadata: metadata ? {[item.toolCall.toolCallId]: metadata} : undefined,
          }
        })
        const toolGroup: ToolGroup<EventToolCallEntry> = {
          id: group.id,
          count: group.items.length,
          metadata: {toolName: group.toolName, isSetupTool: group.isSetupGroup},
          entries,
        }

        return (
          <ToolGroupRenderer key={group.items[0]?.id ?? toolGroup.id} group={toolGroup} groupHeaderFn={groupHeaderFn}>
            {group.items.map(item => (
              <ToolCall
                key={item.id}
                item={item}
                isInGroup
                isEditGroup={isEditGroup}
                mcpToolNameMappings={mcpToolNameMappings}
              />
            ))}
          </ToolGroupRenderer>
        )
      })}
    </div>
  )
}
